'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { BottomSheetShell } from '@/components/ui/bottom-sheet-shell';
import {
  ToastStackPortal,
  type ToastStackSlot,
} from '@/components/workspace/toast-stack';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Legacy escape hatch: extra classes on a free-floating desktop
   * card wrapper. Only used when `desktopStackSlot` is not set, so
   * the call site can position itself outside the shared top-left
   * toast column (rare — we want every prompt in the same stack
   * now).
   */
  desktopClassName?: string;
  /**
   * When set, render into the shared `ToastStackProvider` column
   * on desktop (top-left, under the BrandCard). On mobile the
   * legacy docked sheet still wins — both because the stack
   * portal returns null and because the column wouldn't fit a
   * full-width drag-to-dismiss sheet anyway. Pass `'critical'` to
   * sit above other slots (payment / tool-approval), `'default'`
   * for everything else.
   */
  desktopStackSlot?: ToastStackSlot;
  /**
   * Render above app modals rather than under them. The toast column and the
   * default mobile overlay both sit at z-50, which is below the z-[200] used
   * by dialogs, so a sheet opened FROM a dialog (Settings -> Cookie
   * preferences) would otherwise be visible but unclickable. Only set this
   * for sheets a user can deliberately summon while a dialog is open.
   */
  elevated?: boolean;
}

export function BottomSheet({
  open,
  onClose,
  children,
  desktopClassName = 'fixed bottom-4 left-4 z-50 max-w-sm',
  desktopStackSlot,
  elevated = false,
}: BottomSheetProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragOffsetY = useRef(0);
  const isDragging = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    setIsMobile(!mq.matches);
    setMounted(true);
    const handler = (e: MediaQueryListEvent) => setIsMobile(!e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    isDragging.current = true;
    dragStartY.current = e.touches[0].clientY;
    dragOffsetY.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current || !sheetRef.current) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    dragOffsetY.current = Math.max(0, dy);
    sheetRef.current.style.transform = `translateY(${dragOffsetY.current}px)`;
  }, []);

  const handleTouchEnd = useCallback(() => {
    isDragging.current = false;
    if (!sheetRef.current) return;
    if (dragOffsetY.current > 100) {
      onClose();
    } else {
      sheetRef.current.style.transform = '';
    }
    dragOffsetY.current = 0;
  }, [onClose]);

  if (!mounted || !open) return null;

  if (!isMobile) {
    // New default: render into the shared top-left toast column.
    // Slide-in direction flips from `from-bottom` to `from-top` so
    // the entry animation matches where the card actually lives.
    if (desktopStackSlot) {
      return (
        <ToastStackPortal slot={desktopStackSlot}>
          <div className="animate-in slide-in-from-top-4 fade-in duration-300">
            <BottomSheetShell>{children}</BottomSheetShell>
          </div>
        </ToastStackPortal>
      );
    }
    return (
      <div className={`${desktopClassName} animate-in slide-in-from-bottom-4 fade-in duration-300`}>
        <BottomSheetShell>{children}</BottomSheetShell>
      </div>
    );
  }

  return (
    // z-[110] clears the workspace chrome the sheet would otherwise sit under
    // on a phone — the mobile tab bar (z-96) covered the consent buttons — while
    // staying below app dialogs (z-200). `elevated` still climbs above those.
    <div className={elevated ? 'fixed inset-0 z-[300]' : 'fixed inset-0 z-[110]'}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      {/* Positioning lives on a WRAPPER, never on the shell. The shell's own
          base classes start with `relative`, and Tailwind emits `.relative`
          after `.absolute`, so an `absolute` passed through `className` loses
          the cascade no matter how the attribute is ordered — the sheet then
          sat at the TOP of the overlay on phones. (critical-toast already
          wraps for this reason.) The wrapper also carries the drag transform
          so the whole sheet moves with the finger. */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 animate-in slide-in-from-bottom duration-300 transition-transform"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <BottomSheetShell radiusMode="docked" showHandle contentClassName="px-5 pb-6 pt-1">
          {children}
        </BottomSheetShell>
      </div>
    </div>
  );
}
